import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

import { useWine } from '@/features/wines/useWines';
import { BigChip, BigOption } from './wizard/BigOption';
import { ColorGlassPicker } from './wizard/ColorGlassPicker';
import {
  ACIDEZ, ALCOHOL, AROMAS_PRIMARIOS, AROMAS_SECUNDARIOS, AROMAS_TERCIARIOS,
  BURBUJA, CLARIDAD, COLOR_BLANCO, COLOR_ROSADO, COLOR_TINTO, CONDICION_NARIZ,
  CUERPO, DULZOR, ESCALA_5, EVOLUCION, FINAL, INTENSIDAD_ASPECTO,
  NIVEL_CALIDAD, POTENCIAL_GUARDA, TANINO,
} from './tastingOptions';
import type {
  AromaGroup, BocaData, ConclusionData, NarizData, TastingNote, VistaData,
} from './tastingOptions';
import { useSaveTastingNote, useTastingNote } from './useTastingNotes';

interface FormState {
  tasted_on: string;
  vista: VistaData;
  nariz: NarizData;
  boca: BocaData;
  conclusion: string;
  conclusion_data: ConclusionData;
  score: number | '';
  scale: '100' | '20' | 'propio';
}

const today = () => new Date().toISOString().slice(0, 10);

const empty: FormState = {
  tasted_on: today(),
  vista: {},
  nariz: { aromas_primarios: [], aromas_secundarios: [], aromas_terciarios: [] },
  boca: {},
  conclusion: '',
  conclusion_data: {},
  score: '',
  scale: '100',
};

const STEP_TITLES = [
  'Aspecto',
  'Nariz · estructura',
  'Nariz · aromas',
  'Boca · estructura',
  'Boca · taninos y burbuja',
  'Boca · sabor y final',
  'Conclusión',
];

export function TastingWizard() {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const noteId = params.id ? Number(params.id) : undefined;
  const wineIdFromQuery = searchParams.get('wine_id');
  const { data: editing } = useTastingNote(noteId);

  const wineId = noteId ? editing?.wine_id : wineIdFromQuery ? Number(wineIdFromQuery) : undefined;
  const { data: wine } = useWine(wineId);
  const save = useSaveTastingNote();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(empty);

  useEffect(() => {
    if (editing) {
      setForm({
        tasted_on: editing.tasted_on,
        vista: editing.vista ?? {},
        nariz: editing.nariz ?? {
          aromas_primarios: [],
          aromas_secundarios: [],
          aromas_terciarios: [],
        },
        boca: editing.boca ?? {},
        conclusion: editing.conclusion ?? '',
        conclusion_data: editing.conclusion_data ?? {},
        score: editing.score ?? '',
        scale: editing.scale,
      });
    }
  }, [editing]);

  const totalSteps = STEP_TITLES.length;

  // Saltar paso "tanino y burbuja" si el vino no es tinto ni espumante.
  const skipStep5 = useMemo(() => {
    if (!wine) return false;
    return wine.type !== 'tinto' && wine.type !== 'espumante';
  }, [wine]);

  const next = () => {
    let n = step + 1;
    if (n === 5 && skipStep5) n = 6;
    setStep(Math.min(totalSteps, n));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const prev = () => {
    let n = step - 1;
    if (n === 5 && skipStep5) n = 4;
    setStep(Math.max(1, n));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!wineId) return <div className="p-4 md:p-8 text-notion-500">Sin vino seleccionado.</div>;
  if (!wine) return <div className="p-4 md:p-8 text-notion-500">Cargando vino…</div>;

  const updateVista = (patch: Partial<VistaData>) =>
    setForm((f) => ({ ...f, vista: { ...f.vista, ...patch } }));
  const updateNariz = (patch: Partial<NarizData>) =>
    setForm((f) => ({ ...f, nariz: { ...f.nariz, ...patch } }));
  const updateBoca = (patch: Partial<BocaData>) =>
    setForm((f) => ({ ...f, boca: { ...f.boca, ...patch } }));
  const updateConc = (patch: Partial<ConclusionData>) =>
    setForm((f) => ({ ...f, conclusion_data: { ...f.conclusion_data, ...patch } }));

  const submit = async () => {
    const payload: Partial<TastingNote> = {
      wine_id: wineId,
      tasted_on: form.tasted_on,
      vista: form.vista,
      nariz: form.nariz,
      boca: form.boca,
      conclusion: form.conclusion || null,
      conclusion_data: form.conclusion_data,
      score: form.score === '' ? null : Number(form.score),
      scale: form.scale,
    };
    const saved = await save.mutateAsync({ id: noteId, payload });
    navigate(`/wines/${saved.wine_id}`);
  };

  // Pintamos los 3 grupos de colores siempre. El grupo del tipo del vino
  // aparece primero (el resto sirve para casos atípicos: blancos viejos
  // muy ambarinos, rosados oxidados, etc.).
  const colorGroups = (() => {
    const groups = [
      { title: 'Blanco', options: COLOR_BLANCO },
      { title: 'Rosado', options: COLOR_ROSADO },
      { title: 'Tinto', options: COLOR_TINTO },
    ];
    if (wine.type === 'tinto') return [groups[2], groups[0], groups[1]];
    if (wine.type === 'rosado') return [groups[1], groups[0], groups[2]];
    return groups;
  })();
  const isRed = wine.type === 'tinto';
  const isSparkling = wine.type === 'espumante';

  return (
    <div className="p-4 md:p-8 max-w-2xl pb-32 md:pb-8">
      <button
        type="button"
        onClick={() => navigate(`/wines/${wineId}`)}
        className="text-sm text-notion-500 inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={14} /> Volver al vino
      </button>

      <header className="mb-4">
        <h1 className="text-xl md:text-2xl font-medium leading-tight">
          {noteId ? 'Editar cata' : 'Nueva cata'}
        </h1>
        <p className="text-sm text-notion-500">
          {wine.name} · {wine.vintage ?? '—'} · {wine.format}
        </p>
        <p className="text-[11px] text-notion-400 tracking-wide uppercase mt-1">
          Técnica Sistemática de Cata WSET® · Nivel 3
        </p>
      </header>

      {/* Progreso */}
      <Progress current={step} total={totalSteps} title={STEP_TITLES[step - 1]} />

      <div className="mt-5 space-y-5">
        {step === 1 && (
          <StepAspecto
            vista={form.vista}
            updateVista={updateVista}
            colorGroups={colorGroups}
          />
        )}
        {step === 2 && (
          <StepNarizEstructura nariz={form.nariz} updateNariz={updateNariz} />
        )}
        {step === 3 && (
          <StepAromas nariz={form.nariz} updateNariz={updateNariz} />
        )}
        {step === 4 && (
          <StepBocaEstructura boca={form.boca} updateBoca={updateBoca} />
        )}
        {step === 5 && (
          <StepTaninosBurbuja
            boca={form.boca}
            updateBoca={updateBoca}
            showTaninos={isRed}
            showBurbuja={isSparkling}
          />
        )}
        {step === 6 && (
          <StepBocaSabor boca={form.boca} updateBoca={updateBoca} />
        )}
        {step === 7 && (
          <StepConclusion
            conclusion={form.conclusion}
            conclusionData={form.conclusion_data}
            score={form.score}
            scale={form.scale}
            tastedOn={form.tasted_on}
            updateConc={updateConc}
            setConclusion={(v) => setForm((f) => ({ ...f, conclusion: v }))}
            setScore={(v) => setForm((f) => ({ ...f, score: v }))}
            setScale={(v) => setForm((f) => ({ ...f, scale: v }))}
            setTastedOn={(v) => setForm((f) => ({ ...f, tasted_on: v }))}
          />
        )}
      </div>

      {/* Navegación fija abajo en mobile */}
      <div className="fixed md:static bottom-0 inset-x-0 z-30 bg-white border-t border-notion-200 p-3 md:p-0 md:mt-6 md:border-0">
        <div className="flex items-center gap-2 max-w-2xl mx-auto">
          {step > 1 && (
            <button type="button" className="btn-secondary flex-1 py-3" onClick={prev}>
              <ArrowLeft size={14} /> Atrás
            </button>
          )}
          {step < totalSteps ? (
            <button type="button" className="btn-primary flex-1 py-3 text-base" onClick={next}>
              Siguiente <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary flex-1 py-3 text-base bg-emerald-600 hover:bg-emerald-700"
              onClick={submit}
              disabled={save.isPending}
            >
              <Check size={14} />
              {save.isPending ? 'Guardando…' : 'Guardar cata'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
//  Componentes de cada paso
// ============================================================================

function Progress({ current, total, title }: { current: number; total: number; title: string }) {
  const pct = (current / total) * 100;
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="text-xs text-notion-500 tabular-nums">
          Paso <strong className="text-notion-800">{current}</strong> de {total}
        </p>
        <p className="text-sm font-medium text-notion-800">{title}</p>
      </div>
      <div className="h-1.5 bg-notion-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-emerald-600 transition-all duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <p className="text-xs uppercase tracking-wide text-notion-500 mb-2">{children}</p>;
}

function StepAspecto({
  vista, updateVista, colorGroups,
}: {
  vista: VistaData;
  updateVista: (patch: Partial<VistaData>) => void;
  colorGroups: { title: string; options: { value: string; label: string }[] }[];
}) {
  return (
    <>
      <div>
        <SectionLabel>Claridad</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {CLARIDAD.map((o) => (
            <BigOption
              key={o.value}
              active={vista.claridad === o.value}
              onClick={() => updateVista({ claridad: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Intensidad</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          {INTENSIDAD_ASPECTO.map((o) => (
            <BigOption
              key={o.value}
              active={vista.intensidad === o.value}
              onClick={() => updateVista({ intensidad: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Color</SectionLabel>
        <ColorGlassPicker
          value={vista.color ?? ''}
          onChange={(v) => updateVista({ color: v })}
          groups={colorGroups}
        />
      </div>

      <div>
        <SectionLabel>Otras observaciones</SectionLabel>
        <textarea
          className="input"
          rows={2}
          value={vista.observaciones ?? ''}
          onChange={(e) => updateVista({ observaciones: e.target.value })}
          placeholder="ej. lágrimas, sedimento, burbujas"
        />
      </div>
    </>
  );
}

function StepNarizEstructura({
  nariz, updateNariz,
}: {
  nariz: NarizData;
  updateNariz: (patch: Partial<NarizData>) => void;
}) {
  return (
    <>
      <div>
        <SectionLabel>Condición</SectionLabel>
        <div className="space-y-2">
          {CONDICION_NARIZ.map((o) => (
            <BigOption
              key={o.value}
              active={nariz.condicion === o.value}
              onClick={() => updateNariz({ condicion: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Intensidad</SectionLabel>
        <div className="space-y-2">
          {ESCALA_5.map((o) => (
            <BigOption
              key={o.value}
              active={nariz.intensidad === o.value}
              onClick={() => updateNariz({ intensidad: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Evolución</SectionLabel>
        <div className="space-y-2">
          {EVOLUCION.map((o) => (
            <BigOption
              key={o.value}
              active={nariz.evolucion === o.value}
              onClick={() => updateNariz({ evolucion: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function StepAromas({
  nariz, updateNariz,
}: {
  nariz: NarizData;
  updateNariz: (patch: Partial<NarizData>) => void;
}) {
  const toggle = (group: 'aromas_primarios' | 'aromas_secundarios' | 'aromas_terciarios', v: string) => {
    const current = nariz[group] ?? [];
    const next = current.includes(v) ? current.filter((x) => x !== v) : [...current, v];
    updateNariz({ [group]: next } as Partial<NarizData>);
  };

  return (
    <>
      <AromaSection
        title="Aromas primarios"
        subtitle="De la uva y la fermentación"
        options={AROMAS_PRIMARIOS}
        selected={nariz.aromas_primarios ?? []}
        onToggle={(v) => toggle('aromas_primarios', v)}
      />
      <AromaSection
        title="Aromas secundarios"
        subtitle="De la vinificación posterior"
        options={AROMAS_SECUNDARIOS}
        selected={nariz.aromas_secundarios ?? []}
        onToggle={(v) => toggle('aromas_secundarios', v)}
      />
      <AromaSection
        title="Aromas terciarios"
        subtitle="Del envejecimiento"
        options={AROMAS_TERCIARIOS}
        selected={nariz.aromas_terciarios ?? []}
        onToggle={(v) => toggle('aromas_terciarios', v)}
      />

      <div>
        <SectionLabel>Características del aroma (texto libre)</SectionLabel>
        <textarea
          className="input"
          rows={3}
          value={nariz.caracteristicas ?? ''}
          onChange={(e) => updateNariz({ caracteristicas: e.target.value })}
          placeholder="ej. ciruela negra madura, vainilla, cuero"
        />
      </div>
    </>
  );
}

function AromaSection({
  title, subtitle, options, selected, onToggle,
}: {
  title: string;
  subtitle: string;
  options: AromaGroup[];
  selected: string[];
  onToggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-notion-500">{title}</p>
      <p className="text-[11px] text-notion-400 italic mb-2">{subtitle}</p>
      <div className="flex flex-wrap gap-2">
        {options.map((g) => (
          <BigChip
            key={g.value}
            active={selected.includes(g.value)}
            onClick={() => onToggle(g.value)}
            label={g.label}
          />
        ))}
      </div>
    </div>
  );
}

function StepBocaEstructura({
  boca, updateBoca,
}: {
  boca: BocaData;
  updateBoca: (patch: Partial<BocaData>) => void;
}) {
  return (
    <>
      <div>
        <SectionLabel>Dulzor</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {DULZOR.map((o) => (
            <BigOption
              key={o.value}
              active={boca.dulzor === o.value}
              onClick={() => updateBoca({ dulzor: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Acidez</SectionLabel>
        <div className="space-y-2">
          {ACIDEZ.map((o) => (
            <BigOption
              key={o.value}
              active={boca.acidez === o.value}
              onClick={() => updateBoca({ acidez: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Alcohol</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          {ALCOHOL.map((o) => (
            <BigOption
              key={o.value}
              active={boca.alcohol === o.value}
              onClick={() => updateBoca({ alcohol: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Cuerpo</SectionLabel>
        <div className="space-y-2">
          {CUERPO.map((o) => (
            <BigOption
              key={o.value}
              active={boca.cuerpo === o.value}
              onClick={() => updateBoca({ cuerpo: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>
    </>
  );
}

function StepTaninosBurbuja({
  boca, updateBoca, showTaninos, showBurbuja,
}: {
  boca: BocaData;
  updateBoca: (patch: Partial<BocaData>) => void;
  showTaninos: boolean;
  showBurbuja: boolean;
}) {
  return (
    <>
      {showTaninos && (
        <div>
          <SectionLabel>Tanino</SectionLabel>
          <div className="space-y-2">
            {TANINO.map((o) => (
              <BigOption
                key={o.value}
                active={boca.tanino === o.value}
                onClick={() => updateBoca({ tanino: o.value })}
                label={o.label}
              />
            ))}
          </div>
        </div>
      )}

      {showBurbuja && (
        <div>
          <SectionLabel>Burbuja</SectionLabel>
          <div className="grid grid-cols-3 gap-2">
            {BURBUJA.map((o) => (
              <BigOption
                key={o.value}
                active={boca.burbuja === o.value}
                onClick={() => updateBoca({ burbuja: o.value })}
                label={o.label}
              />
            ))}
          </div>
        </div>
      )}

      {!showTaninos && !showBurbuja && (
        <p className="text-sm text-notion-500 text-center py-8">
          Este paso no aplica para este vino. Tocá <strong>Siguiente</strong> para continuar.
        </p>
      )}
    </>
  );
}

function StepBocaSabor({
  boca, updateBoca,
}: {
  boca: BocaData;
  updateBoca: (patch: Partial<BocaData>) => void;
}) {
  return (
    <>
      <div>
        <SectionLabel>Intensidad del sabor</SectionLabel>
        <div className="space-y-2">
          {ESCALA_5.map((o) => (
            <BigOption
              key={o.value}
              active={boca.intensidad_sabor === o.value}
              onClick={() => updateBoca({ intensidad_sabor: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Final</SectionLabel>
        <div className="space-y-2">
          {FINAL.map((o) => (
            <BigOption
              key={o.value}
              active={boca.final === o.value}
              onClick={() => updateBoca({ final: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Características del sabor (texto libre)</SectionLabel>
        <textarea
          className="input"
          rows={3}
          value={boca.caracteristicas ?? ''}
          onChange={(e) => updateBoca({ caracteristicas: e.target.value })}
          placeholder="ej. fruta negra madura, especias, roble bien integrado"
        />
      </div>
    </>
  );
}

function StepConclusion({
  conclusion, conclusionData, score, scale, tastedOn,
  updateConc, setConclusion, setScore, setScale, setTastedOn,
}: {
  conclusion: string;
  conclusionData: ConclusionData;
  score: number | '';
  scale: '100' | '20' | 'propio';
  tastedOn: string;
  updateConc: (patch: Partial<ConclusionData>) => void;
  setConclusion: (v: string) => void;
  setScore: (v: number | '') => void;
  setScale: (v: '100' | '20' | 'propio') => void;
  setTastedOn: (v: string) => void;
}) {
  return (
    <>
      <div>
        <SectionLabel>Nivel de calidad</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {NIVEL_CALIDAD.map((o) => (
            <BigOption
              key={o.value}
              active={conclusionData.nivel_calidad === o.value}
              onClick={() => updateConc({ nivel_calidad: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Estado para el consumo / potencial guarda</SectionLabel>
        <div className="space-y-2">
          {POTENCIAL_GUARDA.map((o) => (
            <BigOption
              key={o.value}
              active={conclusionData.potencial_guarda === o.value}
              onClick={() => updateConc({ potencial_guarda: o.value })}
              label={o.label}
            />
          ))}
        </div>
      </div>

      <div>
        <SectionLabel>Conclusión / razones (opcional)</SectionLabel>
        <textarea
          className="input"
          rows={4}
          value={conclusion}
          onChange={(e) => setConclusion(e.target.value)}
          placeholder="Razones que justifican el nivel de calidad, recomendación, maridajes…"
        />
      </div>

      <div className="grid grid-cols-3 gap-2 items-end">
        <div className="col-span-2">
          <SectionLabel>Puntaje (opcional)</SectionLabel>
          <input
            type="number"
            min={0}
            max={100}
            inputMode="numeric"
            className="input text-2xl tabular-nums text-center"
            value={score}
            onChange={(e) => setScore(e.target.value === '' ? '' : Number(e.target.value))}
            placeholder="—"
          />
        </div>
        <div>
          <SectionLabel>Escala</SectionLabel>
          <select
            className="input"
            value={scale}
            onChange={(e) => setScale(e.target.value as '100' | '20' | 'propio')}
          >
            <option value="100">/100</option>
            <option value="20">/20</option>
            <option value="propio">propia</option>
          </select>
        </div>
      </div>

      <div>
        <SectionLabel>Fecha de la cata</SectionLabel>
        <input
          type="date"
          className="input"
          value={tastedOn}
          max={today()}
          onChange={(e) => setTastedOn(e.target.value)}
        />
      </div>
    </>
  );
}
